/* eslint-disable no-underscore-dangle */
import { faSearch } from "@fortawesome/pro-regular-svg-icons";
import { TextField } from "@/aviaryTmp/TextField/TextField";
import { useState, useEffect, useRef, type ChangeEvent, type FocusEvent } from "react";
import { algoliasearch, type Algoliasearch, type Hit } from "algoliasearch";
import { Typography } from "@fullscript/aviary-web";
import { Link } from "@/aviaryTmp/Link/Link";
import { useLocation } from "@remix-run/react";
import * as styles from "./SearchDocuments.styles";

export type SearchTarget = "Getting started" | "How-to guides" | "Technical reference";

type Hierarchy = {
  lvl0?: string;
  lvl1?: string;
  lvl2?: string;
};

const stripDomain = (url: string) => {
  // Aviary Link component will open in a new tab if the url has https:// in it.
  // Remove entire domain to prevent opening in new tab.
  // Removing domain url will also keep this working on local dev environment.
  // Response from Algolia will always have https://fullscript.dev in the url. It is indexed after production deployment
  return url.replace("https://fullscript.dev", "");
};

type SearchResult = {
  objectID: string;
  value: string;
  url: string;
  hierarchy: Hierarchy;
  tag: SearchTarget;
};

type AlgoliaHit = Hit<{
  url: string;
  tags: string[];
  hierarchy: Hierarchy;
  _snippetResult?: {
    content?: {
      value?: string;
    };
  };
}>;

type NormalizedSearchResults = Record<string, SearchResult[]>;

type AlgoliaHierarchy = Record<
  string,
  {
    value: string;
    matchedWords: string[];
  }
>;

const getMatchedTitle = (hit: AlgoliaHit): string => {
  if (!hit._highlightResult) return "";
  const hierarchy = hit._highlightResult["hierarchy"] as AlgoliaHierarchy;
  const lvl0 = hierarchy["lvl0"];
  const lvl1 = hierarchy["lvl1"];
  const lvl2 = hierarchy["lvl2"];

  if (lvl0?.matchedWords.length) return lvl0.value;
  if (lvl1?.matchedWords.length) return lvl1.value;
  if (lvl2?.matchedWords.length) return lvl2.value;
  return "";
};

// Algolia custom settings
const HITS_PER_PAGE = 10;
const ATTRIBUTES_TO_RETRIEVE = ["anchor", "content", "hierarchy", "tags", "url"];
const MAX_SEARCH_QUERY_LENGTH = 512;

type Props = {
  searchTargets: SearchTarget[];
};

const SearchDocuments = ({ searchTargets }: Props) => {
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [normalizedSearchResults, setNormalizedSearchResults] =
    useState<NormalizedSearchResults | null>(null);
  const clientRef = useRef<Algoliasearch | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const appId = window.ENV.ALGOLIA_APP_ID;
    const apiKey = window.ENV.ALGOLIA_API_SEARCH_KEY;

    const client = algoliasearch(appId, apiKey);

    clientRef.current = client;
  }, []);

  useEffect(() => {
    setSearch("");
    setNormalizedSearchResults(null);
  }, [location.pathname]);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  // Closes the dropdown, but does not reset the search value
  const closeSearch = () => {
    setNormalizedSearchResults(null);
  };

  const searchBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (!searchContainerRef.current?.contains(e.relatedTarget as Node)) {
      closeSearch();
    }
  };
  const searchActive = () => {
    if (search !== "" && normalizedSearchResults === null) {
      onDebouncedChange(search);
    }
  };

  const onDebouncedChange = async (value: string) => {
    if (!value) {
      setNormalizedSearchResults(null);
      return;
    }

    const indexName = window.ENV.ALGOLIA_INDEX_NAME;
    const client = clientRef.current;
    if (value && client && indexName) {
      const algoliaResult = await client.searchSingleIndex<AlgoliaHit>({
        indexName,
        searchParams: {
          query: value,
          hitsPerPage: HITS_PER_PAGE,
          attributesToRetrieve: ATTRIBUTES_TO_RETRIEVE,
        },
      });

      const hits =
        algoliaResult?.hits.map(hit => ({
          objectID: hit.objectID,
          value: hit._snippetResult?.["content"]?.value || getMatchedTitle(hit),
          url: hit.url,
          hierarchy: hit.hierarchy,
          tag: hit.tags?.[0] ?? "Getting started",
        })) ?? [];

      const normalizedResults = hits.reduce((hash, obj) => {
        const key = obj.tag;
        // NOTE: Remove technical reference from the search results because it should be a separate scope in the new fullscript.dev
        // Ideally, we should have a separate index for technical reference, but for backward compatibility with the old fullscript.dev,
        // we're keeping it in the same index until we finish the migration.
        if (!searchTargets.includes(key as SearchTarget)) return hash;
        return { ...hash, [key]: [...(hash[key] ?? []), obj] } as NormalizedSearchResults;
      }, {} as NormalizedSearchResults);

      setNormalizedSearchResults(normalizedResults);
    }
  };

  const renderSearchContent = (result: SearchResult) => {
    const url = stripDomain(result.url);

    const { lvl0, lvl1, lvl2 } = result.hierarchy;
    const concatenatedHierarchy = [lvl0, lvl1, lvl2].filter(v => !!v).join(" > ");

    return (
      <li key={result.objectID}>
        <Link href={url} isUnderLined={false} css={styles.searchResultLink} onClick={closeSearch}>
          <span css={styles.searchResultTitle} dangerouslySetInnerHTML={{ __html: result.value }} />
          <span css={styles.searchResultHierarchy}>{concatenatedHierarchy}</span>
        </Link>
      </li>
    );
  };

  return (
    <div css={styles.searchContainer} ref={searchContainerRef} onBlur={searchBlur}>
      <TextField
        id="search"
        key="search"
        type="text"
        value={search}
        maxLength={MAX_SEARCH_QUERY_LENGTH}
        inputIcon={faSearch}
        placeholder="Search documentation"
        onChange={onChange}
        onDebouncedChange={onDebouncedChange}
        onFocus={searchActive}
        isDebounced
      />
      {normalizedSearchResults && (
        <div data-testid="search-documents-results" css={styles.searchResultContainer}>
          {Object.keys(normalizedSearchResults).map(key => (
            <div key={key} css={styles.searchResultGroup}>
              <Typography variant="caption" isMarginless customCss={styles.searchResultGroupName}>
                {key}
              </Typography>
              <ul>{normalizedSearchResults[key].map(result => renderSearchContent(result))}</ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export { SearchDocuments };
/* eslint-enable no-underscore-dangle */
